import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Alert } from 'react-bootstrap';
import { bridgeService } from '../lib/bridgeService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../components/BridgeHistoryStyles.scss';

const AdminBridgeDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'approve', 'complete', 'reject'
  const [adminNotes, setAdminNotes] = useState('');
  const [solanaTxHash, setSolanaTxHash] = useState('');
  const [stats, setStats] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await loadBridgeRequests();
    await loadBridgeStats();
  };

  const loadBridgeRequests = async () => {
    try {
      setLoading(true);
      const allRequests = await bridgeService.getAllBridgeRequests();
      
      // Sort by creation date (newest first)
      allRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setRequests(allRequests);
      
    } catch (error) {
      console.error('Error loading bridge requests:', error);
      showAlert('Error loading bridge requests', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const loadBridgeStats = async () => {
    try {
      const bridgeStats = await bridgeService.getBridgeStats();
      setStats(bridgeStats);
    } catch (error) {
      console.error('Error loading bridge stats:', error);
    }
  };

  const showAlert = (message, type = 'info') => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => {
      setAlertMessage('');
      setAlertType('');
    }, 5000);
  };

  const handleAction = (request, action) => {
    setSelectedRequest(request);
    setModalType(action);
    setAdminNotes('');
    setSolanaTxHash('');
    setShowModal(true);
  };

  const executeAction = async () => {
    if (!selectedRequest) return;
    
    setActionLoading(true);
    try {
      let result;
      
      switch (modalType) {
        case 'approve':
          result = await bridgeService.adminApproveBridgeRequest(selectedRequest.id, adminNotes);
          showAlert('Bridge request approved successfully', 'success');
          break;
          
        case 'complete':
          if (!solanaTxHash.trim()) {
            const txType = selectedRequest.direction === 'bsc-to-solana' ? 'Solana' : 'BSC';
            showAlert(`Please enter the ${txType} transaction hash`, 'danger');
            return;
          }
          result = await bridgeService.adminCompleteBridgeRequest(selectedRequest.id, solanaTxHash, adminNotes);
          showAlert('Bridge request marked as completed', 'success');
          break;
          
        case 'reject':
          if (!adminNotes.trim()) {
            showAlert('Please enter a reason for rejection', 'danger');
            return;
          }
          result = await bridgeService.adminRejectBridgeRequest(selectedRequest.id, adminNotes);
          showAlert('Bridge request rejected', 'info');
          break;
          
        default:
          return;
      }
      
      console.log('Action result:', result);
      
      // Reload data
      await loadData();
      
      // Close modal
      setShowModal(false);
      
    } catch (error) {
      console.error('Error executing action:', error);
      showAlert(`Error: ${error.message}`, 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'approved':
        return <Badge bg="info">Approved</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending Review</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const canApprove = (status) => status === 'pending';
  const canComplete = (status) => status === 'approved';
  const canReject = (status) => status === 'pending' || status === 'approved';

  return (
    <div className="bridge-history-page">
      <Header />
      
      <Container className="bridge-history-container mt-5 mb-5">
        <div className="text-center mb-4">
          <h2 className="page-title">Admin Bridge Dashboard</h2>
          <p className="page-subtitle">Manage and process bridge requests</p>
        </div>

        {alertMessage && (
          <Alert variant={alertType} dismissible onClose={() => setAlertMessage('')}>
            {alertMessage}
          </Alert>
        )}

        {/* Statistics */}
        {stats && (
          <Row className="mb-4">
            <Col md={2}>
              <Card className="stat-card">
                <Card.Body className="text-center">
                  <h3 className="stat-number">{stats.total}</h3>
                  <p className="stat-label">Total</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="stat-card">
                <Card.Body className="text-center">
                  <h3 className="stat-number">{stats.pending}</h3>
                  <p className="stat-label">Pending</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="stat-card">
                <Card.Body className="text-center">
                  <h3 className="stat-number">{stats.approved}</h3>
                  <p className="stat-label">Approved</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="stat-card">
                <Card.Body className="text-center">
                  <h3 className="stat-number">{stats.completed}</h3>
                  <p className="stat-label">Completed</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="stat-card">
                <Card.Body className="text-center">
                  <h3 className="stat-number">{stats.rejected}</h3>
                  <p className="stat-label">Rejected</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="stat-card">
                <Card.Body className="text-center">
                  <h3 className="stat-number">{stats.totalVolume.toFixed(2)}</h3>
                  <p className="stat-label">Volume (DBN)</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Requests Table */}
        <Row>
          <Col>
            <Card className="transactions-card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Bridge Requests</h5>
                <Button variant="outline-light" size="sm" onClick={loadData}>
                  <i className="fas fa-refresh me-1"></i>
                  Refresh
                </Button>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">No bridge requests found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Request ID</th>
                          <th>From Address</th>
                          <th>To Address</th>
                          <th>Amount</th>
                          <th>Source Tx</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((request, index) => (
                          <tr key={index}>
                            <td>{formatDate(request.createdAt)}</td>
                            <td>
                              <code className="small">{request.id}</code>
                            </td>
                            <td>
                              <Badge bg={request.direction === 'bsc-to-solana' ? 'warning' : 'info'} className="mb-1">
                                {request.direction === 'bsc-to-solana' ? 'BSC → Solana' : 'Solana → BSC'}
                              </Badge>
                              <br />
                              <code>{formatAddress(
                                request.direction === 'bsc-to-solana' 
                                  ? request.fromAddress 
                                  : request.fromSolanaAddress
                              )}</code>
                            </td>
                            <td>
                              <code>{formatAddress(
                                request.direction === 'bsc-to-solana' 
                                  ? request.toSolanaAddress 
                                  : request.toBSCAddress
                              )}</code>
                            </td>
                            <td>
                              <strong>{parseFloat(request.amount || 0).toFixed(4)} DBN</strong>
                              <div className="text-muted small">
                                Net: {parseFloat(request.netAmount || 0).toFixed(4)} DBN
                              </div>
                            </td>
                            <td>
                              <a 
                                href={
                                  request.direction === 'bsc-to-solana' 
                                    ? `https://bscscan.com/tx/${request.bscTxHash}`
                                    : `https://explorer.solana.com/tx/${request.solanaSignature}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-decoration-none"
                              >
                                <code className="small">{formatAddress(
                                  request.direction === 'bsc-to-solana' 
                                    ? request.bscTxHash 
                                    : request.solanaSignature
                                )}</code>
                              </a>
                            </td>
                            <td>{getStatusBadge(request.status)}</td>
                            <td>
                              <div className="btn-group-vertical btn-group-sm">
                                {canApprove(request.status) && (
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleAction(request, 'approve')}
                                    className="mb-1"
                                  >
                                    Approve
                                  </Button>
                                )}
                                {canComplete(request.status) && (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleAction(request, 'complete')}
                                    className="mb-1"
                                  >
                                    Complete
                                  </Button>
                                )}
                                {canReject(request.status) && (
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleAction(request, 'reject')}
                                  >
                                    Reject
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Action Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === 'approve' && 'Approve Bridge Request'}
            {modalType === 'complete' && 'Complete Bridge Request'}
            {modalType === 'reject' && 'Reject Bridge Request'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <h6>Request Details:</h6>
              <ul className="list-unstyled">
                <li><strong>ID:</strong> {selectedRequest.id}</li>
                <li><strong>From:</strong> {selectedRequest.fromAddress}</li>
                <li><strong>To:</strong> {selectedRequest.toSolanaAddress}</li>
                <li><strong>Amount:</strong> {selectedRequest.amount} DBN</li>
                <li><strong>Net Amount:</strong> {selectedRequest.netAmount} DBN</li>
                <li><strong>BSC Tx:</strong> {selectedRequest.bscTxHash}</li>
                <li><strong>Status:</strong> {selectedRequest.status}</li>
              </ul>

              <hr />

              {modalType === 'complete' && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    {selectedRequest?.direction === 'bsc-to-solana' ? 'Solana' : 'BSC'} Transaction Hash *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={`Enter ${selectedRequest?.direction === 'bsc-to-solana' ? 'Solana' : 'BSC'} transaction hash`}
                    value={solanaTxHash}
                    onChange={(e) => setSolanaTxHash(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    {selectedRequest?.direction === 'bsc-to-solana' 
                      ? "Enter the Solana transaction hash after manually sending DBN tokens to the user's Solana address."
                      : "Enter the BSC transaction hash after manually sending DBN tokens to the user's BSC address."
                    }
                  </Form.Text>
                  
                  <Alert variant="info" className="mt-2">
                    <h6>Manual Process:</h6>
                    <ol className="mb-0">
                      {selectedRequest?.direction === 'bsc-to-solana' ? (
                        <>
                          <li>Use your Solana wallet (Phantom, etc.) to send <strong>{selectedRequest?.netAmount} DBN</strong></li>
                          <li>Send to: <code>{selectedRequest?.toSolanaAddress}</code></li>
                          <li>Copy the transaction signature and paste it above</li>
                        </>
                      ) : (
                        <>
                          <li>Use your BSC wallet (MetaMask, etc.) to send <strong>{selectedRequest?.netAmount} DBN</strong></li>
                          <li>Send to: <code>{selectedRequest?.toBSCAddress}</code></li>
                          <li>Copy the transaction hash and paste it above</li>
                        </>
                      )}
                    </ol>
                  </Alert>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>
                  Admin Notes {modalType === 'reject' && '*'}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={
                    modalType === 'reject' 
                      ? "Enter reason for rejection" 
                      : "Enter any notes (optional)"
                  }
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant={
              modalType === 'approve' ? 'success' : 
              modalType === 'complete' ? 'primary' : 'danger'
            }
            onClick={executeAction}
            disabled={actionLoading}
          >
            {actionLoading ? 'Processing...' : 
              modalType === 'approve' ? 'Approve Request' :
              modalType === 'complete' ? 'Mark as Completed' : 'Reject Request'
            }
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </div>
  );
};

export default AdminBridgeDashboard;
